import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, DataTable, Card, Input, Localized, Select } from "../../components/admin/Fields";
import ImageUpload from "../../components/admin/ImageUpload";
import SEOFields from "../../components/admin/SEOFields";

const empty = () => ({
  slug: "",
  name: { tr: "", en: "", ar: "" },
  location: { tr: "", en: "", ar: "" },
  country: "",
  category: "commercial",
  status: "completed",
  business_unit_slug: "",
  group_company_slug: "",
  year: "",
  client: "",
  area_sqm: "",
  description: { tr: "", en: "", ar: "" },
  cover_image: "",
  gallery: [],
  featured: false,
  seo: {},
});

const CATEGORIES = [
  { value: "residential", label: "Konut" },
  { value: "commercial", label: "Ticari" },
  { value: "infrastructure", label: "Altyapı" },
  { value: "hospitality", label: "Otelcilik" },
  { value: "mixed-use", label: "Karma Kullanım" },
  { value: "industrial", label: "Endüstriyel" },
];
const STATUSES = [
  { value: "completed", label: "Tamamlandı" },
  { value: "ongoing", label: "Devam Ediyor" },
  { value: "upcoming", label: "Yakında" },
];

const AdminProjectsPage = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/projects").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing({ new: true });
    setForm(empty());
  };
  const startEdit = (r) => {
    setEditing(r);
    setForm({ ...empty(), ...r });
  };
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        area_sqm: form.area_sqm ? Number(form.area_sqm) : null,
        gallery: Array.isArray(form.gallery)
          ? form.gallery
          : String(form.gallery || "")
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean),
      };
      if (editing?.new) {
        await api.post("/admin/projects", payload);
      } else {
        await api.put(`/admin/projects/${editing.id}`, payload);
      }
      toast.success("Kaydedildi");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`"${r.slug}" silinsin mi?`)) return;
    await api.delete(`/admin/projects/${r.id}`);
    toast.success("Silindi");
    load();
  };

  if (editing) {
    return (
      <div data-testid="admin-project-form">
        <AdminHeader
          eyebrow="Projects"
          title={editing.new ? "Yeni Proje" : "Projeyi Düzenle"}
          action={
            <div className="flex gap-3">
              <Btn variant="ghost" onClick={cancel} testId="project-cancel">İptal</Btn>
              <Btn onClick={save} disabled={busy} testId="project-save">Kaydet</Btn>
            </div>
          }
        />
        <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card title="Genel Bilgiler">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="project-slug" />
                <Input label="Yıl" type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testId="project-year" />
                <Input label="Müşteri" value={form.client} onChange={(v) => setForm({ ...form, client: v })} testId="project-client" />
                <Input label="Alan (m²)" type="number" value={form.area_sqm} onChange={(v) => setForm({ ...form, area_sqm: v })} testId="project-area" />
                <Input label="Ülke Kodu (TR/AE...)" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testId="project-country" />
                <Input label="Faaliyet Alanı slug" value={form.business_unit_slug} onChange={(v) => setForm({ ...form, business_unit_slug: v })} testId="project-bu" />
                <Input label="Grup Şirketi slug" value={form.group_company_slug} onChange={(v) => setForm({ ...form, group_company_slug: v })} testId="project-group-company" placeholder="firat-real-estate" />
                <Select label="Kategori" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} testId="project-category" />
                <Select label="Durum" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES} testId="project-status" />
              </div>
              <div className="mt-6">
                <Localized label="Proje Adı" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="project-name" />
              </div>
              <div className="mt-6">
                <Localized label="Konum" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="project-location" />
              </div>
              <div className="mt-6">
                <Localized label="Açıklama" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea testId="project-desc" />
              </div>
            </Card>
          </div>
          <div className="space-y-6">
            <Card title="Görseller">
              <ImageUpload label="Kapak Görseli" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testId="project-cover-upload" />
              <div className="mt-4">
                <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">Galeri</div>
                <div className="space-y-3">
                  {(Array.isArray(form.gallery) ? form.gallery : []).map((g, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="flex-1"><ImageUpload label={`Görsel ${i + 1}`} value={g} onChange={(v) => setForm({ ...form, gallery: form.gallery.map((x, j) => (j === i ? v : x)) })} /></div>
                      <Btn variant="danger" onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, j) => j !== i) })}><Trash2 size={12} /></Btn>
                    </div>
                  ))}
                  <Btn variant="ghost" onClick={() => setForm({ ...form, gallery: [...(form.gallery || []), ""] })} testId="add-gallery"><Plus size={12} /> Görsel ekle</Btn>
                </div>
              </div>
            </Card>
            <Card title="Yayın">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  data-testid="project-featured"
                />
                <span className="text-sm">Öne çıkarılsın</span>
              </label>
            </Card>
            <SEOFields
              value={form.seo}
              onChange={(seo) => setForm({ ...form, seo })}
              context={{
                title: form.name?.tr || form.name?.en || form.slug,
                content: [form.location?.tr, form.description?.tr, form.description?.en, form.specifications?.tr].filter(Boolean).join(" \n").slice(0, 3000),
                category: "project",
                slug: `projects/${form.slug}`,
              }}
            />
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-projects-page">
      <AdminHeader
        eyebrow="Projects"
        title="Projeler"
        action={<Btn onClick={startNew} testId="new-project"><Plus size={14} /> Yeni Proje</Btn>}
      />
      <DataTable
        columns={[
          { key: "name", label: "Ad", render: (r) => r.name?.tr || r.name?.en || r.slug },
          { key: "category", label: "Kategori" },
          { key: "status", label: "Durum" },
          { key: "year", label: "Yıl" },
          {
            key: "actions",
            label: "",
            render: (r) => (
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={(e) => { e.stopPropagation(); startEdit(r); }} testId={`edit-${r.id}`}>Düzenle</Btn>
                <Btn variant="danger" onClick={(e) => { e.stopPropagation(); remove(r); }} testId={`delete-${r.id}`}><Trash2 size={12} /></Btn>
              </div>
            ),
          },
        ]}
        rows={rows}
      />
    </div>
  );
};

export default AdminProjectsPage;
